import PropTypes from "prop-types"
import React, { useCallback, useEffect, useRef, useState } from "react"

// //Import Scrollbar
import SimpleBar from "simplebar-react"

// MetisMenu
import MetisMenu from "metismenujs"
import withRouter from "../Common/withRouter"
import { Link, useLocation } from "react-router-dom"

//i18n
import { withTranslation } from "react-i18next"
// AskAIButton is now rendered in main layouts (VerticalLayout/HorizontalLayout) to avoid duplicates
import { usePrefetchOnHover } from "../../utils/prefetchManager"
import { Star, ChevronRight } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { blogApiUrl } from "../../utils/variables"

// Default fallback ads
function getDefaultAds() {
  return [
    {
      name: "Beat Saber",
      tagline: "Rhythm gaming redefined",
      category: "Music",
      rating: 4.8,
      image: require("../../assets/images/beatsaber.jpg"),
      targetUrl: '#'
    },
    {
      name: "VR Fitness Pro",
      tagline: "Your personal VR trainer",
      category: "Fitness",
      rating: 4.6,
      image: require("../../assets/images/a1.png"),
      targetUrl: '#'
    },
    {
      name: "Space Explorer",
      tagline: "Journey through the cosmos",
      category: "Adventure",
      rating: 4.5,
      image: require("../../assets/images/a2.png"),
      targetUrl: '#'
    }
  ]
}

// Shared singleton store for the sponsored ad widget. SponsoredAdCard is
// mounted in two places at once (desktop sidebar + mobile nav drawer, only
// one of which is visible at any given viewport width), so this keeps both
// showing the same ad in sync and ensures only one network fetch and one
// rotation timer ever run, instead of one per mounted instance.
let sponsoredAdStore = { promoApps: [], loading: true, currentIndex: 0, direction: 1 }
const sponsoredAdListeners = new Set()
let sponsoredAdFetchStarted = false
let sponsoredAdRotationStarted = false

function updateSponsoredAdStore(partial) {
  sponsoredAdStore = { ...sponsoredAdStore, ...partial }
  sponsoredAdListeners.forEach(listener => listener(sponsoredAdStore))
}

function ensureSponsoredAdFetch() {
  if (sponsoredAdFetchStarted) return
  sponsoredAdFetchStarted = true

  const fetchAds = async () => {
    try {
      const response = await fetch(`${blogApiUrl}/internal-ads/api/internal-ads/ads/active/`)
      if (!response.ok) throw new Error('Failed to fetch ads')

      const data = await response.json()

      // Transform API response to match component structure
      const transformedAds = data.map(ad => {
        const gameData = ad.game_data || {}
        return {
          id: ad.id,
          name: gameData.name || ad.title,
          tagline: ad.description ? ad.description.substring(0, 40) + '...' : 'Sponsored game',
          category: gameData.category || 'Games',
          rating: gameData.rating?.average ? parseFloat(gameData.rating.average.toFixed(1)) : 4.5,
          image: ad.image_url || gameData.screenshots?.[0] || gameData.icon || require("../../assets/images/beatsaber.jpg"),
          targetUrl: ad.target_url || '#',
          adType: ad.ad_type
        }
      })

      updateSponsoredAdStore({ promoApps: transformedAds, loading: false })
    } catch (err) {
      console.error('Error fetching ads:', err)
      // Fallback to default ads on error
      updateSponsoredAdStore({ promoApps: getDefaultAds(), loading: false })
    }
  }

  fetchAds()
}

function ensureSponsoredAdRotation() {
  if (sponsoredAdRotationStarted) return
  sponsoredAdRotationStarted = true

  setInterval(() => {
    if (sponsoredAdStore.promoApps.length === 0) return
    updateSponsoredAdStore({
      currentIndex: (sponsoredAdStore.currentIndex + 1) % sponsoredAdStore.promoApps.length,
      direction: 1,
    })
  }, 5000)
}

function useSponsoredAdStore() {
  const [state, setState] = useState(sponsoredAdStore)

  useEffect(() => {
    sponsoredAdListeners.add(setState)
    ensureSponsoredAdFetch()
    ensureSponsoredAdRotation()
    return () => {
      sponsoredAdListeners.delete(setState)
    }
  }, [])

  return state
}

// Matches the CSS breakpoint that switches between the desktop sidebar
// (.display-desktop-nav) and the mobile nav drawer (.main_mobile): desktop
// at >=1200px, mobile at <1200px. Used so only the currently-visible
// SponsoredAdCard instance actually renders (see below) instead of both
// existing in the DOM with only CSS telling them apart.
function useIsDesktopViewport() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" ? window.innerWidth >= 1200 : true
  )

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1200)
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  return isDesktop
}

// Sponsored Ad Card Component
// `variant` identifies which layout slot this instance lives in ("desktop"
// sidebar or "mobile" nav drawer). Only the instance matching the current
// viewport actually renders - the other returns null - so the ad card (and
// its heading) exists in the DOM once at a time, not twice with one hidden
// by CSS.
// Slide variants for the ad carousel — `direction` (1 = forward, -1 = backward) picks
// which side the incoming card enters from and the outgoing one exits to, so it reads
// as a horizontal carousel (auto-advancing or swiped) rather than a plain crossfade.
const adSlideVariants = {
  enter: (direction) => ({ x: direction > 0 ? 40 : -40, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction) => ({ x: direction > 0 ? -40 : 40, opacity: 0 }),
}

// How far (px) a swipe has to travel before it counts as "go to the next/previous ad"
// instead of snapping back — matches typical mobile carousel swipe thresholds.
const AD_SWIPE_THRESHOLD = 50

function SponsoredAdCard({ variant = "desktop" }) {
  const isDesktop = useIsDesktopViewport()
  const { promoApps, loading, currentIndex, direction } = useSponsoredAdStore()

  const goToIndex = (nextIndex, nextDirection) => {
    if (promoApps.length === 0) return
    const wrapped = ((nextIndex % promoApps.length) + promoApps.length) % promoApps.length
    updateSponsoredAdStore({ currentIndex: wrapped, direction: nextDirection })
  }

  const shouldRender = variant === "desktop" ? isDesktop : !isDesktop
  if (!shouldRender) {
    return null
  }

  if (loading) {
    return (
      <div style={{ margin: "16px 12px 24px", padding: "20px", textAlign: "center" }}>
        <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "12px" }}>Loading ads...</div>
      </div>
    )
  }

  if (promoApps.length === 0) {
    return null
  }

  const app = promoApps[currentIndex]
  const hasMultiple = promoApps.length > 1

  return (
    <div style={{ margin: "12px 8px 8px" }}>
      <AnimatePresence mode="wait" custom={direction} initial={false}>
        <motion.div
          key={currentIndex}
          custom={direction}
          variants={adSlideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.3, ease: "easeOut" }}
          // Manual swipe: more ads than one can be dragged horizontally left/right to
          // move through them — the same gesture the auto-rotation timer drives.
          drag={hasMultiple ? "x" : false}
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.6}
          onDragEnd={(_event, info) => {
            if (!hasMultiple) return
            if (info.offset.x <= -AD_SWIPE_THRESHOLD) {
              goToIndex(currentIndex + 1, 1)
            } else if (info.offset.x >= AD_SWIPE_THRESHOLD) {
              goToIndex(currentIndex - 1, -1)
            }
          }}
          style={{
            position: "relative",
            overflow: "hidden",
            borderRadius: "12px",
            cursor: "pointer",
            backgroundColor: "#f8f9fa",
            touchAction: hasMultiple ? "pan-y" : "auto"
          }}
          className="group"
          onClick={() => {
            if (app.targetUrl && app.targetUrl !== '#') {
              window.open(app.targetUrl, '_blank')
            }
          }}
        >
          {/* Background image */}
          <img
            src={app.image}
            alt={app.name}
            style={{
              width: "100%",
              height: "144px",
              objectFit: "cover",
              transition: "transform 0.5s ease"
            }}
            className="group-hover:scale-110"
            onError={(e) => {
              e.target.src = require("../../assets/images/beatsaber.jpg")
            }}
          />

          {/* Gradient overlay */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.4), transparent)"
            }}
          />

          {/* Sponsored badge */}
          <div
            style={{
              position: "absolute",
              top: "8px",
              left: "8px"
            }}
          >
            <span
              style={{
                fontSize: "9px",
                textTransform: "uppercase",
                letterSpacing: "1px",
                fontWeight: "bold",
                backgroundColor: "#f6255a",
                color: "white",
                padding: "2px 8px",
                borderRadius: "12px"
              }}
            >
              Sponsored
            </span>
          </div>

          {/* Content */}
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              padding: "12px"
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "4px"
              }}
            >
              <span
                style={{
                  fontSize: "10px",
                  color: "rgba(255,255,255,0.8)"
                }}
              >
                {app.category}
              </span>
              <span
                style={{
                  fontSize: "10px",
                  color: "rgba(255,255,255,0.5)"
                }}
              >
                ·
              </span>
              <span
                style={{
                  fontSize: "10px",
                  color: "#fbbf24",
                  display: "flex",
                  alignItems: "center",
                  gap: "2px"
                }}
              >
                <Star size={10} fill="currentColor" />
                {app.rating}
              </span>
            </div>
            <div
              style={{
                fontSize: "14px",
                fontWeight: "bold",
                color: "white",
                lineHeight: "1.2",
                margin: "0 0 2px 0"
              }}
            >
              {app.name}
            </div>
            <p
              style={{
                fontSize: "11px",
                color: "rgba(255,255,255,0.6)",
                margin: "0 0 8px 0"
              }}
            >
              {app.tagline}
            </p>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "11px",
                color: "#f6255a",
                fontWeight: "500",
                transition: "gap 0.2s ease"
              }}
              className="group-hover:gap-2"
            >
              Track Now
              <ChevronRight size={12} />
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Dots indicator */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "6px",
          marginTop: "8px"
        }}
      >
        {promoApps.map((_, i) => (
          <button
            key={i}
            onClick={(e) => {
              e.stopPropagation()
              goToIndex(i, i > currentIndex ? 1 : -1)
            }}
            style={{
              height: "4px",
              borderRadius: "2px",
              transition: "all 0.3s ease",
              border: "none",
              background: i === currentIndex ? "#f6255a" : "rgba(156, 163, 175, 0.3)",
              width: i === currentIndex ? "16px" : "6px",
              cursor: "pointer"
            }}
            onMouseEnter={(e) => {
              if (i !== currentIndex) {
                e.target.style.background = "rgba(156, 163, 175, 0.5)"
              }
            }}
            onMouseLeave={(e) => {
              if (i !== currentIndex) {
                e.target.style.background = "rgba(156, 163, 175, 0.3)"
              }
            }}
          />
        ))}
      </div>
    </div>
  )
}

const SidebarContent = props => {
  const { isCollapsed = false } = props
  // const ref = useRef();

  // Prefetch hooks for different routes
  const dashboardPrefetch = usePrefetchOnHover('/dashboard')
  const trendingPrefetch = usePrefetchOnHover('/trending')
  const newReleasePrefetch = usePrefetchOnHover('/new-releases')
  const topAppsPrefetch = usePrefetchOnHover('/top-apps')
  const popularPrefetch = usePrefetchOnHover('/popular')
  const mostPopularPrefetch = usePrefetchOnHover('/most-popular')
  const preOrdersPrefetch = usePrefetchOnHover('/pre-orders')
  const topDiscountPrefetch = usePrefetchOnHover('/top-discounts')
  const topGrossPrefetch = usePrefetchOnHover('/topgross')
  const topImprovedPrefetch = usePrefetchOnHover('/topimproved')
  const TopKeywordsCategoriesPrefetch = usePrefetchOnHover('/top-keywords-categories')
  const topReviewedPrefetch = usePrefetchOnHover('/top-reviewed')
  const topRatedPrefetch = usePrefetchOnHover('/top-rated')
  const topDownloadedPrefetch = usePrefetchOnHover('/top-downloaded')
  const topGenres2025Prefetch = usePrefetchOnHover('/top-genres-2025')

  // Market Research prefetch hooks
  const keywordSearchPrefetch = usePrefetchOnHover('/keyword-analysis')
  const keywordSuggestionsPrefetch = usePrefetchOnHover('/keyword-suggestions')
  const socialMediaComparisonPrefetch = usePrefetchOnHover('/social-media-comparison')
  const ugcExplorerPrefetch = usePrefetchOnHover('/ugc-explorer')
  const influencerExplorerPrefetch = usePrefetchOnHover('/influencer-explorer')
  const categoryAnalysisPrefetch = usePrefetchOnHover('/categoryanalysis')
  const appTrackingPrefetch = usePrefetchOnHover('/app-tracking')
  const keywordTrackingPrefetch = usePrefetchOnHover('/keyword-tracking')
  const categoryTrackingPrefetch = usePrefetchOnHover('/category-tracking')
  const publisherAnalysisPrefetch = usePrefetchOnHover('/publisheranalysis')

  // AI Research prefetch hooks
  const chatbotPrefetch = usePrefetchOnHover('/chatbot')

  // Profile prefetch hooks
  const profileSettingsPrefetch = usePrefetchOnHover('/profile/settings')
  const profileBillingPrefetch = usePrefetchOnHover('/profile')

  // Track whether any global tutorial overlay is active (keyword/app tracking/profile/Ask AI)
  const [hasTutorialOverlay, setHasTutorialOverlay] = useState(false)
  const [localCollapsedState, setLocalCollapsedState] = useState(isCollapsed)

  // Tooltip / submenu-flyout state for the collapsed (icon-only) sidebar.
  // `items`, when present, switches the popover from a plain-text tooltip to a
  // clickable list of the hovered item's children (e.g. "Weekly Insights" ->
  // Trending/New Release/...) — there's no room to show them inline once the
  // sidebar is down to icon width, so they show as a flyout instead.
  const [tooltip, setTooltip] = useState({ show: false, text: '', items: null, x: 0, y: 0 })
  // Closing is debounced (rather than instant on mouseleave) so the pointer can
  // travel from the icon to the flyout below/right of it without the flyout
  // disappearing first — both the trigger and the flyout itself cancel this timer
  // on re-entry and restart it on leave.
  const hideTimeoutRef = useRef(null)

  const cancelPendingHide = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }

  // Tooltip / flyout handlers. `items` is an optional array of { label, to } for
  // menu items that have children — omit it for plain leaf items.
  const handleMouseEnter = (e, text, items = null) => {
    if (!localCollapsedState) return
    cancelPendingHide()
    const rect = e.currentTarget.getBoundingClientRect()
    setTooltip({
      show: true,
      text,
      items,
      x: rect.right,
      // A single-line tooltip looks best vertically centered on the icon; a
      // multi-item flyout reads better top-aligned with it instead.
      y: items && items.length > 0 ? rect.top : rect.top + rect.height / 2 - 12
    })
  }

  const handleMouseLeave = () => {
    cancelPendingHide()
    hideTimeoutRef.current = setTimeout(() => {
      setTooltip({ show: false, text: '', items: null, x: 0, y: 0 })
    }, 150)
  }

  // Keeps the flyout open while the pointer is over the flyout panel itself.
  const handleFlyoutMouseEnter = () => {
    cancelPendingHide()
  }

  // Top-aligning the flyout with the hovered icon (see handleMouseEnter) means
  // items near the bottom of the sidebar (e.g. "Custom Insights") can overflow
  // past the bottom of the screen. This ref callback runs right after the flyout
  // is committed to the DOM but before the browser paints, so it can measure the
  // flyout's actual rendered height and, if it overflows, nudge it up just enough
  // to fit — same timing `useLayoutEffect` relies on, without an extra render.
  const adjustFlyoutPosition = (el) => {
    if (!el) return
    const rect = el.getBoundingClientRect()
    const overflow = rect.bottom - window.innerHeight + 8 // 8px breathing room
    if (overflow > 0) {
      setTooltip((prev) => {
        if (!prev.show) return prev
        const newY = Math.max(8, prev.y - overflow)
        if (Math.abs(newY - prev.y) < 1) return prev
        return { ...prev, y: newY }
      })
    }
  }

  useEffect(() => {
    return () => cancelPendingHide()
  }, [])

  // Sync local state with prop when it changes
  useEffect(() => {
    setLocalCollapsedState(isCollapsed)
  }, [isCollapsed])

  // Also watch DOM for collapsed state changes (fallback)
  useEffect(() => {
    const body = document.body
    const checkCollapsedState = () => {
      // Treat the classic "vertical-collpsed" class as the signal here.
      // This keeps the visual/clickable menu items exactly as the theme expects.
      const isNowCollapsed = body.classList.contains("vertical-collpsed")
      setLocalCollapsedState(isNowCollapsed)
    }

    const observer = new MutationObserver(checkCollapsedState)
    observer.observe(body, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: false
    })

    return () => observer.disconnect()
  }, [])

  // Note: Ask AI tutorial is now handled by AskAIButton component in the main layouts
  const activateParentDropdown = useCallback(item => {
    // Get the direct parent <li> of the anchor
    const itemParentLi = item.parentElement
    if (!itemParentLi) return false

    // Check if this is a sub-menu item (parent is <li> inside <ul class="sub-menu">)
    const subMenuUl = itemParentLi.parentElement
    const isSubMenuItem = subMenuUl && subMenuUl.classList.contains("sub-menu")

    if (isSubMenuItem) {
      // This is a sub-menu item
      // 1. Remove active/mm-active from ALL sub-menu items in ALL sub-menus first
      const ul = document.getElementById("side-menu")
      if (ul) {
        const allSubMenus = ul.querySelectorAll("ul.sub-menu")
        allSubMenus.forEach(menu => {
          const allSubMenuItems = menu.querySelectorAll("li a")
          allSubMenuItems.forEach(subItem => {
            subItem.classList.remove("active")
            subItem.parentElement.classList.remove("mm-active")
          })
        })
      }

      // 2. Remove active/mm-active from ALL main menu items (direct children of #side-menu)
      if (ul) {
        const allMainMenuLis = ul.children
        for (let i = 0; i < allMainMenuLis.length; i++) {
          const mainLi = allMainMenuLis[i]
          if (mainLi.tagName === "LI") {
            mainLi.classList.remove("mm-active")
            const mainAnchor = mainLi.firstElementChild
            if (mainAnchor && mainAnchor.tagName === "A") {
              mainAnchor.classList.remove("active")
              mainAnchor.classList.remove("mm-active")
            }
          }
        }
      }

      // 3. Now add active to the clicked sub-menu item
      item.classList.add("active")
      itemParentLi.classList.add("mm-active")

      // 4. Add mm-show to the sub-menu <ul> to expand it
      subMenuUl.classList.add("mm-show")

      // 5. Get the parent <li> that contains the sub-menu
      const parentLi = subMenuUl.parentElement
      if (parentLi) {
        // Remove mm-active and active from parent main menu item (only keep it expanded)
        parentLi.classList.remove("mm-active")
        const parentAnchor = parentLi.firstElementChild
        if (parentAnchor && parentAnchor.tagName === "A") {
          parentAnchor.classList.remove("mm-active")
          parentAnchor.classList.remove("active")
        }

        // 6. If there's a nested structure, expand parent menus too (but don't highlight them)
        let currentParent = parentLi.parentElement
        while (currentParent && currentParent.id !== "side-menu") {
          if (currentParent.tagName === "UL") {
            currentParent.classList.add("mm-show")
          }
          currentParent = currentParent.parentElement
        }
      }
    } else {
      // This is a top-level/main menu item
      const ul = document.getElementById("side-menu")

      // 1. Remove active/mm-active from ALL sub-menu items in ALL sub-menus
      if (ul) {
        const allSubMenus = ul.querySelectorAll("ul.sub-menu")
        allSubMenus.forEach(subMenu => {
          const allSubMenuItems = subMenu.querySelectorAll("li a")
          allSubMenuItems.forEach(subItem => {
            subItem.classList.remove("active")
            subItem.parentElement.classList.remove("mm-active")
          })
        })
      }

      // 2. Remove active/mm-active from ALL other main menu items (except the clicked one)
      if (ul) {
        const allMainMenuLis = ul.children
        for (let i = 0; i < allMainMenuLis.length; i++) {
          const mainLi = allMainMenuLis[i]
          if (mainLi.tagName === "LI" && mainLi !== itemParentLi) {
            // This is a different main menu item, remove its highlight
            mainLi.classList.remove("mm-active")
            const mainAnchor = mainLi.firstElementChild
            if (mainAnchor && mainAnchor.tagName === "A") {
              mainAnchor.classList.remove("active")
              mainAnchor.classList.remove("mm-active")
            }
            // Also collapse its sub-menu if it has one
            const otherSubMenu = mainLi.querySelector("ul.sub-menu")
            if (otherSubMenu) {
              otherSubMenu.classList.remove("mm-show")
            }
          }
        }
      }

      // 3. Check if this main menu has a sub-menu
      const subMenu = itemParentLi.querySelector("ul.sub-menu")
      if (subMenu) {
        // Keep sub-menu expanded for this main menu
        subMenu.classList.add("mm-show")
      }

      // 4. Add active and mm-active to the clicked main menu item
      item.classList.add("active")
      itemParentLi.classList.add("mm-active")
    }

    return false
  }, [])

  const removeActivation = items => {
    // Remove all active and mm-active classes from all menu items
    const ul = document.getElementById("side-menu")
    if (!ul) return

    // Remove active class from all anchors
    for (var i = 0; i < items.length; ++i) {
      var item = items[i]
      if (item && item.classList.contains("active")) {
        item.classList.remove("active")
      }
    }

    // Remove mm-active from all <li> elements
    const allLis = ul.querySelectorAll("li")
    for (let i = 0; i < allLis.length; i++) {
      allLis[i].classList.remove("mm-active")
    }

    // Remove mm-active from all anchor elements
    const allAnchors = ul.querySelectorAll("a")
    for (let i = 0; i < allAnchors.length; i++) {
      allAnchors[i].classList.remove("mm-active")
    }

    // Don't remove mm-show here - let activateParentDropdown handle it
    // This prevents blinking when switching between menu items
  }

  const path = useLocation()

  // Note: Ask AI tutorial is now handled by AskAIButton component in the main layouts
  // Removed duplicate tutorial logic from SidebarContent to prevent duplicate tutorials
  const activeMenu = useCallback(() => {
    // Get pathname without query params or hash
    const pathName = path.pathname.split("?")[0].split("#")[0]
    let matchingMenuItem = null
    const ul = document.getElementById("side-menu")
    if (!ul) return

    const items = ul.getElementsByTagName("a")
    removeActivation(items)

    // Normalize current pathname (remove trailing slash except for root)
    const normalizedPathName = pathName === "/" ? "/" : pathName.replace(/\/$/, "")

    // Custom mapping for detail pages that should highlight parent menu items
    const detailToParentMapping = [
      { path: '/xr/keyword', parent: '/keyword-tracking' },
      { path: '/xr/category', parent: '/category-tracking' },
      { path: '/xr/', parent: '/app-tracking' }  // This will match any other /xr/{category}/ path
    ]

    // Check if current path is a detail page that should map to a parent menu
    let targetPath = normalizedPathName
    for (const { path: detailPath, parent: parentPath } of detailToParentMapping) {
      if (normalizedPathName.startsWith(detailPath)) {
        targetPath = parentPath
        break
      }
    }

    for (let i = 0; i < items.length; ++i) {
      const anchor = items[i]

      // Try multiple methods to get the path from the anchor element
      // React Router Link might set pathname property or href attribute
      let linkPath = null

      // Method 1: Get href attribute first (most reliable for React Router)
      const href = anchor.getAttribute("href")

      if (href && href !== "#") {
        // Extract pathname from href
        try {
          if (href.startsWith("/")) {
            // Relative path - remove query params and hash
            linkPath = href.split("?")[0].split("#")[0]
          } else if (href.startsWith("http")) {
            // Full URL - extract pathname
            const url = new URL(href)
            linkPath = url.pathname
          }
        } catch (e) {
          // If URL parsing fails, try to extract path manually
          linkPath = href.split("?")[0].split("#")[0]
        }
      }

      // Method 2: Fallback to pathname property if href didn't work
      if (!linkPath && anchor.pathname) {
        // Remove query params and hash from pathname too
        linkPath = anchor.pathname.split("?")[0].split("#")[0]
      }

      if (!linkPath || linkPath === "#") continue

      // Normalize link path (remove trailing slash except for root)
      const normalizedLinkPath = linkPath === "/" ? "/" : linkPath.replace(/\/$/, "")

      // Check for exact match first
      if (targetPath === normalizedLinkPath) {
        matchingMenuItem = anchor
        break
      }

      // Check if current path starts with the menu path (for detail pages)
      // This handles cases like /keyword-tracking/123 should highlight /keyword-tracking
      if (targetPath.startsWith(normalizedLinkPath + '/')) {
        matchingMenuItem = anchor
        break
      }
    }

    if (matchingMenuItem) {
      activateParentDropdown(matchingMenuItem)

      // The active item can land anywhere in a long (possibly newly-expanded)
      // submenu, well below the fold of the sidebar's scroll area — without this it
      // stayed selected but invisible until the user scrolled down manually to find
      // it. Delayed past MetisMenu's submenu expand transition (`mm-collapsing`,
      // 0.35s in _vertical.scss) so scrollIntoView measures the settled layout
      // instead of a mid-animation one, which can under/overshoot the target.
      setTimeout(() => {
        matchingMenuItem.scrollIntoView({ behavior: "smooth", block: "center" })
      }, 380)
    }
  }, [path.pathname, activateParentDropdown])

  // useEffect(() => {
  //   ref.current.recalculate();
  // }, []);

  // Watch DOM for global tutorial backdrops and visually blur the sidebar while active
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return

    const checkOverlay = () => {
      try {
        const hasOverlay = !!document.querySelector(".kt-backdrop-overlay, .profile-tutorial-backdrop-overlay")
        setHasTutorialOverlay(hasOverlay)
      } catch (e) {
        // ignore
      }
    }

    // Initial check
    checkOverlay()

    // Observe DOM mutations to react when overlays are added/removed
    const observer = new MutationObserver(() => {
      checkOverlay()
    })

    try {
      observer.observe(document.body, { childList: true, subtree: true })
    } catch (e) {
      // ignore observe errors
    }

    // Also listen to keyword tutorial custom events as an extra signal
    window.addEventListener("keywordTutorialStep", checkOverlay)
    window.addEventListener("skipAllKeywordTutorial", checkOverlay)

    return () => {
      observer.disconnect()
      window.removeEventListener("keywordTutorialStep", checkOverlay)
      window.removeEventListener("skipAllKeywordTutorial", checkOverlay)
    }
  }, [])

  useEffect(() => {
    // Small delay to ensure DOM is fully ready before initializing MetisMenu
    setTimeout(() => {
      new MetisMenu("#side-menu")
    }, 100)
  }, [])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" })
    // Small delay to ensure DOM is ready and MetisMenu has initialized
    const timer = setTimeout(() => {
      activeMenu()
    }, 150)
    return () => clearTimeout(timer)
  }, [activeMenu])

  // function scrollElement(item) {
  //   if (item) {
  //     const currentPosition = item.offsetTop;
  //     if (currentPosition > window.innerHeight) {
  //       ref.current.getScrollElement().scrollTop = currentPosition - 300;
  //     }
  //   }
  // }
  return (
    <React.Fragment>
      {/* The scrollable menu. This component no longer owns the sponsored ad card —
          it now lives in Sidebar.js as its own flex sibling, alongside the header,
          inside `.vertical-menu` — so this is free to just fill whatever height
          `.sidebar-menu-scroll` (its parent, sized by flex) gives it. */}
      <SimpleBar
        style={{
          height: '100%',
          // Slight blur for visual focus when any tutorial overlay is active
          filter: hasTutorialOverlay ? "blur(3px)" : "none",
          // Also make the sidebar non-interactive while tutorial is active
          pointerEvents: hasTutorialOverlay ? "none" : "auto",
        }}
      >
        <div id="sidebar-menu">
          <ul className="metismenu list-unstyled" id="side-menu">
              <li>
                <Link
                  to="/dashboard"
                  className="waves-effect"
                  title={props.t("Home")}
                  {...dashboardPrefetch}
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("Home"))}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil-home-alt"></i>
                  <span className="badge rounded-pill bg-primary float-end">
                  </span>
                  <span>{props.t("Home")}</span>
                </Link>
              </li>

              <li>
                <Link
                  to="/blog"
                  className="waves-effect"
                  title={props.t("Blog")}
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("Blog"))}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil-newspaper"></i>
                  <span>{props.t("Blog")}</span>
                </Link>
              </li>

              <li>
                <Link
                  to="/ai-cmo"
                  className="waves-effect"
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("AI CMO"))}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil-search"></i>
                  <span>{props.t("AI CMO")}</span>
                </Link>
              </li>

              <li>
                <Link
                  to="/store-validator"
                  className="waves-effect"
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("Store Validator"))}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil-shield-check"></i>
                  <span>{props.t("Store Validator")}</span>
                </Link>
              </li>

              <li className="menu-title">
                <span style={{
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  color: 'var(--bs-sidebar-menu-item-icon-color)',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  display: 'block',
                  opacity: 0.7,
                  textAlign: 'left',
                  paddingLeft: '20px !important',
                  paddingTop: '4px',
                  paddingBottom: '4px',
                  paddingRight: '20px',
                  margin: 0
                }}>
                  {props.t("Market Research")}
                </span>
              </li>

              {/* <li>
                <Link to="/keyword-search" className=" waves-effect">
                  <i className="uil-calender"></i>
                  <span>{props.t("App Keyword Explorer")}</span>
                </Link>
              </li> */}
              <li>
                <Link
                  to="/keyword-suggestions"
                  className=" waves-effect"
                  title={props.t("Keyword Suggestions")}
                  {...keywordSuggestionsPrefetch}
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("Keyword Suggestions"))}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil-lightbulb"></i>
                  <span>{props.t("Keyword Suggestions")}</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/category-explorer"
                  className="waves-effect"
                  title={props.t("Category Explorer")}
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("Category Explorer"))}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil-lightbulb"></i>
                  <span>{props.t("Category Explorer")}</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/keyword-analysis"
                  className=" waves-effect"
                  title={props.t("Keyword Explorer")}
                  {...keywordSearchPrefetch}
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("Keyword Explorer"))}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil-search"></i>
                  <span>{props.t("Keyword Explorer")}</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/category-analysis"
                  className=" waves-effect"
                  {...categoryAnalysisPrefetch}
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("Genre Explorer"))}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil-layers"></i>
                  <span>{props.t("Genre Explorer")}</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/publisher-analysis"
                  className=" waves-effect"
                  {...publisherAnalysisPrefetch}
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("Publisher Explorer"))}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil-chart-line"></i>
                  <span>{props.t("Publisher Explorer")}</span>
                </Link>
              </li>
              <li className="menu-title">
                <span style={{
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  color: 'var(--bs-sidebar-menu-item-icon-color)',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  display: 'block',
                  opacity: 0.7,
                  textAlign: 'left',
                  paddingLeft: '20px !important',
                  paddingTop: '4px',
                  paddingBottom: '4px',
                  paddingRight: '20px',
                  margin: 0
                }}>
                  {props.t("Brand Research")}
                </span>
              </li>
              <li>
                <Link
                  to="/social-media-comparison"
                  className=" waves-effect"
                  title={props.t("Social Comparison")}
                  {...socialMediaComparisonPrefetch}
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("Social Comparison"))}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil-share-alt"></i>
                  <span>{props.t("Social Comparison")}</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/ugc-explorer"
                  className=" waves-effect"
                  title={props.t("UGC Explorer")}
                  {...ugcExplorerPrefetch}
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("UGC Explorer"))}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil-video"></i>
                  <span>{props.t("UGC Explorer")}</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/influencer-explorer"
                  className=" waves-effect"
                  title={props.t("Influencer Explorer")}
                  {...influencerExplorerPrefetch}
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("Influencer Explorer"))}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil-users-alt"></i>
                  <span>{props.t("Influencer Explorer")}</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/creative-insights"
                  className="waves-effect"
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("Ad Discovery"))}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil uil-trophy"></i>
                  <span>{props.t("Ad Discovery")}</span>
                </Link>
              </li>
              <li className="menu-title">
                <span style={{
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  color: 'var(--bs-sidebar-menu-item-icon-color)',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  display: 'block',
                  opacity: 0.7,
                  textAlign: 'left',
                  paddingLeft: '20px !important',
                  paddingTop: '4px',
                  paddingBottom: '4px',
                  paddingRight: '20px',
                  margin: 0
                }}>
                  {props.t("TRACKING & PERFORMANCE")}
                </span>
              </li>
              <li>
                <Link
                  to="/app-tracking"
                  className=" waves-effect"
                  {...appTrackingPrefetch}
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("App Tracking"))}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil-chart-growth"></i>
                  <span>{props.t("App Tracking")}</span>
                </Link>
              </li>

              {/* <li>
                <Link to="/keyword-tracking-old" className=" waves-effect">
                  <i className="uil-calender"></i>
                  <span>{props.t("Keyword Tracking")}</span>
                </Link>
              </li> */}
              <li>
                <Link
                  to="/keyword-tracking"
                  className="waves-effect"
                  {...keywordTrackingPrefetch}
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("Keyword Tracking"))}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil-crosshair"></i>

                  <span>{props.t("Keyword Tracking")}</span>
                </Link>
              </li>


              <li>
                <Link
                  to="/category-tracking"
                  className="waves-effect"
                  {...categoryTrackingPrefetch}
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("Genre Tracking"))}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil-layer-group"></i>
                  {/* <span className="badge rounded-pill bg-warning float-end">
                    {" "}
                    {props.t("New")}{" "}
                  </span> */}
                  <span>{props.t("Genre Tracking")}</span>
                </Link>
              </li>

              <li className="menu-title">
                <span style={{
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  color: 'var(--bs-sidebar-menu-item-icon-color)',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  display: 'block',
                  opacity: 0.7,
                  textAlign: 'left',
                  paddingLeft: '20px !important',
                  paddingTop: '4px',
                  paddingBottom: '4px',
                  paddingRight: '20px',
                  margin: 0
                }}>
                  {props.t("INSIGHTS")}
                </span>
              </li>
              <li>
                <Link
                  to="/weekly-insights"
                  className="has-arrow waves-effect"
                  onClick={e => {
                    // prevent default to avoid any native toggles interfering
                    e.preventDefault()
                    // navigate using withRouter injected navigate to ensure SPA routing
                    if (props && props.router && props.router.navigate) {
                      props.router.navigate('/weekly-insights')
                    } else {
                      // fallback
                      window.location.href = '/weekly-insights'
                    }
                  }}
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("Weekly Insights"), [
                    { label: props.t("Trending"), to: "/trending" },
                    { label: props.t("New Release"), to: "/new-releases" },
                    { label: props.t("Top Selling"), to: "/top-apps" },
                    { label: props.t("Popular"), to: "/most-popular" },
                    { label: props.t("Top Discounts"), to: "/top-discounts" },
                    { label: props.t("PreOrders"), to: "/pre-orders" },
                  ])}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil-lightbulb-alt"></i>
                  <span>{props.t("Weekly Insights")}</span>
                </Link>
                <ul className="sub-menu">
                  <li>
                    <Link
                      to="/trending"
                      {...trendingPrefetch}
                      onMouseEnter={(e) => handleMouseEnter(e, props.t("Trending"))}
                      onMouseLeave={handleMouseLeave}
                    >
                      {props.t("Trending")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/new-releases"
                      {...newReleasePrefetch}
                      onMouseEnter={(e) => handleMouseEnter(e, props.t("New Release"))}
                      onMouseLeave={handleMouseLeave}
                    >
                      {props.t("New Release")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/top-apps"
                      {...topAppsPrefetch}
                      onMouseEnter={(e) => handleMouseEnter(e, props.t("Top Selling"))}
                      onMouseLeave={handleMouseLeave}
                    >
                      {props.t("Top Selling")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/most-popular"
                      {...mostPopularPrefetch}
                      onMouseEnter={(e) => handleMouseEnter(e, props.t("Popular"))}
                      onMouseLeave={handleMouseLeave}
                    >
                      {props.t("Popular")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/top-discounts"
                      {...topDiscountPrefetch}
                      onMouseEnter={(e) => handleMouseEnter(e, props.t("Top Discounts"))}
                      onMouseLeave={handleMouseLeave}
                    >
                      {props.t("Top Discounts")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/pre-orders"
                      {...preOrdersPrefetch}
                      onMouseEnter={(e) => handleMouseEnter(e, props.t("PreOrders"))}
                      onMouseLeave={handleMouseLeave}
                    >
                      {props.t("PreOrders")}
                    </Link>
                  </li>
                </ul>
              </li>

              <li>
                <Link
                  to="/custom-insights"
                  className="has-arrow waves-effect"
                  onClick={e => {
                    // prevent default to avoid any native toggles interfering
                    e.preventDefault()
                    // navigate using withRouter injected navigate to ensure SPA routing
                    if (props && props.router && props.router.navigate) {
                      props.router.navigate('/custom-insights')
                    } else {
                      // fallback
                      window.location.href = '/custom-insights'
                    }
                  }}
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("Custom Insights"), [
                    { label: props.t("Top Genres of 2025"), to: "/top-genres-2025" },
                    { label: props.t("Top Revenued"), to: "/top-revenued" },
                    { label: props.t("Top Downloads"), to: "/top-downloaded" },
                    { label: props.t("Top Average Rating"), to: "/top-rated" },
                    { label: props.t("Top App Score"), to: "/all-time-hits" },
                  ])}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil-analytics"></i>
                  <span>{props.t("Custom Insights")}</span>
                  <span className="badge rounded-pill ">
                    {" "}
                    {/* {props.t("New")}{" "} */}
                  </span>
                </Link>
                <ul className="sub-menu">
                  <li>
                    <Link
                      to="/top-genres-2025"
                      {...topGenres2025Prefetch}
                      onMouseEnter={(e) => handleMouseEnter(e, props.t("Top Genres of 2025"))}
                      onMouseLeave={handleMouseLeave}
                    >
                      {props.t("Top Genres of 2025")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/top-revenued"
                      {...topGrossPrefetch}
                      onMouseEnter={(e) => handleMouseEnter(e, props.t("Top Revenued"))}
                      onMouseLeave={handleMouseLeave}
                    >
                      <span className="badge rounded-pill float-end">
                        {" "}
                        {/* {props.t("New")}{" "} */}
                      </span>
                      {props.t("Top Revenued")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/top-downloaded"
                      {...topDownloadedPrefetch}
                      onMouseEnter={(e) => handleMouseEnter(e, props.t("Top Downloads"))}
                      onMouseLeave={handleMouseLeave}
                    >
                      <span className="badge rounded-pill float-end">
                        {" "}
                        {/* {props.t("New")}{" "} */}
                      </span>
                      {props.t("Top Downloads")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/top-rated"
                      {...topRatedPrefetch}
                      onMouseEnter={(e) => handleMouseEnter(e, props.t("Top Average Rating"))}
                      onMouseLeave={handleMouseLeave}
                    >
                      <span className="badge rounded-pill float-end">
                        {" "}
                        {/* {props.t("New")}{" "} */}
                      </span>
                      {props.t("Top Average Rating")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/all-time-hits"
                      {...topImprovedPrefetch}
                      onMouseEnter={(e) => handleMouseEnter(e, props.t("Top App Score"))}
                      onMouseLeave={handleMouseLeave}
                    >
                      {props.t("Top App Score")}
                    </Link>
                  </li>
                  {/* <li>
                    <Link
                      to="/top-keywords-categories"
                      {...TopKeywordsCategoriesPrefetch}
                    >
                      <span className="badge rounded-pill float-end">
                        {" "}
                        {props.t("New")}{" "}
                      </span>
                      {props.t("Top Keywords & Categories")}
                    </Link>
                  </li> */}

                  {/* "/comingsoon" */}
                </ul>
              </li>
              {/* <li>
                <Link to="/#" className="waves-effect">
                  <i className="uil-comments-alt"></i>
                  <span className="badge rounded-pill bg-warning float-end">
                    {" "}
                    {props.t("New")}{" "}
                  </span>
                  <span>{props.t("AI Analysis")}</span>
                </Link>
              </li> */}
              <li className="menu-title">
                <span style={{
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  color: 'var(--bs-sidebar-menu-item-icon-color)',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  display: 'block',
                  opacity: 0.7,
                  textAlign: 'left',
                  paddingLeft: '20px !important',
                  paddingTop: '4px',
                  paddingBottom: '4px',
                  paddingRight: '20px',
                  margin: 0
                }}>
                  {props.t("AI Research")}
                </span>
              </li>

              <li>
                <Link
                  to="/chatbot"
                  className="waves-effect"
                  {...chatbotPrefetch}
                  onMouseEnter={(e) => handleMouseEnter(e, props.t("AI Chat"))}
                  onMouseLeave={handleMouseLeave}
                >
                  <i className="uil-comments-alt"></i>
                  <span className="badge rounded-pill float-end">
                    {" "}
                    {/* {props.t("New")}{" "} */}
                  </span>
                  <span>{props.t("AI Chat")}</span>
                </Link>
              </li>
            </ul>
          </div>
      </SimpleBar>

      {/* Tooltip / submenu-flyout for the collapsed sidebar. Rendered `position: fixed`
          as a sibling of SimpleBar (not a descendant), so it isn't clipped by
          `.sidebar-menu-scroll`'s `overflow: hidden` or SimpleBar's own internal
          scroll wrapper — both of which clip a pure-CSS flyout nested inside them. */}
      {localCollapsedState && tooltip.show && (
        tooltip.items && tooltip.items.length > 0 ? (
          <div
            ref={adjustFlyoutPosition}
            onMouseEnter={handleFlyoutMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{
              position: 'fixed',
              left: tooltip.x,
              top: tooltip.y,
              minWidth: '190px',
              backgroundColor: '#171821',
              borderRadius: '6px',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.35)',
              border: '1px solid #374151',
              zIndex: 9999,
              overflow: 'hidden',
              animation: 'fadeIn 0.15s ease'
            }}
          >
            <div style={{
              padding: '10px 16px',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#9aa5b2',
              borderBottom: '1px solid #2b2d3a',
              whiteSpace: 'nowrap'
            }}>
              {tooltip.text}
            </div>
            <div style={{ padding: '6px 0' }}>
              {tooltip.items.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={handleMouseLeave}
                  className="sidebar-flyout-link"
                  style={{
                    display: 'block',
                    padding: '8px 16px',
                    fontSize: '14px',
                    color: '#d7dce2',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <style>{`
              @keyframes fadeIn {
                from { opacity: 0; transform: translateX(-5px); }
                to { opacity: 1; transform: translateX(0); }
              }
              .sidebar-flyout-link:hover {
                color: #fff !important;
                background: rgba(255, 255, 255, 0.06);
              }
            `}</style>
          </div>
        ) : (
          <div
            style={{
              position: 'fixed',
              left: tooltip.x + 10,
              top: tooltip.y,
              backgroundColor: '#171821',
              color: '#fff',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              border: '1px solid #374151',
              zIndex: 9999,
              pointerEvents: 'none',
              animation: 'fadeIn 0.2s ease'
            }}
          >
            {tooltip.text}
            <style>{`
              @keyframes fadeIn {
                from { opacity: 0; transform: translateX(-5px); }
                to { opacity: 1; transform: translateX(0); }
              }
            `}</style>
          </div>
        )
      )}

      {/* Ask AI button is now rendered in main layouts (VerticalLayout/HorizontalLayout) to avoid duplicates */}
    </React.Fragment>
  )
}

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
}

export default withRouter(withTranslation()(SidebarContent))

export { SponsoredAdCard }
